import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { shellTodoQuery, type TodoItem, type TodoKey } from '../api/shellTodo'
import { APPS_ATTENTION_EVENT } from './AppsAttentionMark'
import './TodoMenu.css'

/*
 * CONTORUL "NEEDS ATTENTION" DIN ANTET (fost "To do").
 *
 * Cerinta coordonatoarei, 6 octombrie 2026: un contor rosu in locul iconitei
 * de sedii ("Office N") - si a celei de descoperire, care spunea tot un "ai de
 * facut ceva" - cu lista pe categorii la clic: fiecare cu cifra ei, o
 * propozitie despre ce inseamna, si drumul la ecranul unde se rezolva.
 *
 * Cifrele vin din `GET /api/v2/shell/todo` (`App\Shell\Todo\HeaderTodo`):
 * numai categoriile cu ceva in ele si numai cele pe care omul are dreptul sa
 * le rezolve. Cu zero peste tot, butonul ramane, fara insigna, si lista spune
 * ca nu e nimic de facut - antetul nu sare dintr-o forma in alta.
 *
 * Tot pe 6 octombrie: fara cuvantul "To do" pe buton - numai triunghiul de
 * atentie cu insigna rosie; "Unknown devices" se cheama "Unassigned devices"
 * (cheia `unknown` ramane in contract), iar "Stations without a user" a iesit
 * cu totul - statiile lui erau oricum in "Unassigned devices".
 *
 * Tot pe 6 octombrie, a doua cerere: iconita e ROSIE cand e ceva de rezolvat
 * (clasa `od-todo-hot`, vezi foaia), si totul se numara NUMAI pe firma aleasa
 * sus in bara, si cu "All companies" - serverul trimite `company: 0` cand nu e
 * aleasa niciuna (sau una la care omul n-are acces), si panoul cere alegerea.
 */

export interface TodoCategory {
  label: string
  hint: string
  href: (item: TodoItem) => string
}

export const TODO_CATEGORIES: Record<TodoKey, TodoCategory> = {
  offices: {
    label: 'Offices with no name',
    hint: 'Created automatically as "Office 1", "Office 2"... Rename them so reports and filters make sense.',
    href: (item) => `/app/settings/customers/companies?company=${item.companyId ?? 0}&panel=offices`,
  },
  unknown: {
    label: 'Unassigned devices',
    hint: 'Machines with an agent that nobody is assigned to. Assign a user so tickets and reports reach a person.',
    href: () => '/app/inventoree/unassigned',
  },
  apps: {
    label: 'Applications to review',
    hint: 'Found by detection and not confirmed yet, or missing an owner, components or escalation contacts.',
    href: () => '/app/applications?attention=1',
  },
  expiring: {
    label: 'Expiring in 30 days',
    hint: 'Support contracts, licenses and certificates that expire within 30 days - or already have.',
    href: () => '/app/applications?tab=expiring',
  },
  discovery: {
    label: 'Network discovery not set up',
    hint: 'No active scan range, no scheduled relay, or no scan in the last 7 days - new devices on the network go unseen.',
    href: () => '/app/settings/softsee/discovery_setup?assistant=1',
  },
}

export const TODO_NO_COMPANY = 'Choose a customer in the company selector to see what needs attention.'

export function todoBadge(total: number): string {
  if (total < 1) return ''
  return total > 99 ? '99+' : String(total)
}

export function todoTitle(total: number): string {
  if (total < 1) return 'Nothing needs attention'
  return total === 1 ? '1 thing needs attention' : `${total} things need attention`
}

export function TodoMenu() {
  const navigate = useNavigate()
  const todo = useQuery(shellTodoQuery)
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLLIElement>(null)

  /* Inchis la clic afara si la Escape, ca restul meniurilor din antet. */
  useEffect(() => {
    if (!open) return
    const away = (event: globalThis.MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    const esc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('click', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  const items = (todo.data?.items ?? []).filter((i) => i.count > 0 && TODO_CATEGORIES[i.key] !== undefined)
  const total = todo.data?.total ?? 0
  const badge = todoBadge(total)
  const noCompany = todo.data?.company === 0

  const go = (item: TodoItem) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setOpen(false)
    switch (item.key) {
      case 'offices':
        void navigate({
          to: '/settings/customers/$tab',
          params: { tab: 'companies' },
          search: { company: item.companyId ?? 0, panel: 'offices' },
        })
        return
      case 'unknown':
        void navigate({ to: '/inventoree/unassigned' })
        return
      case 'apps':
        /* Ca pastila din bara modulului: daca lista e deja pe ecran, banda se
           deschide prin eveniment, nu prin adresa. */
        void Promise.resolve(navigate({ to: '/applications', search: { attention: 1 } })).then(() =>
          window.dispatchEvent(new Event(APPS_ATTENTION_EVENT)),
        )
        return
      case 'expiring':
        /* Fila se alege o data, cand se deschide pagina: de pe lista deja
           deschisa se reincarca, altfel ar ramane pe fila de acum. */
        if (window.location.pathname.replace(/\/+$/, '') === '/app/applications') {
          window.location.assign(TODO_CATEGORIES.expiring.href(item))
          return
        }
        void navigate({ to: '/applications', search: { tab: 'expiring' } })
        return
      case 'discovery':
        void navigate({ to: '/settings/softsee/$tab', params: { tab: 'discovery_setup' }, search: { assistant: 1 } })
        return
    }
  }

  return (
    <li className={`nav-quick-actions od-nav-todo${open ? ' open' : ''}`} ref={box} data-testid="header-todo">
      <a
        href="#"
        id="odTodoToggle"
        className={`od-todo-toggle${total > 0 ? ' od-todo-hot' : ''}`}
        title={todoTitle(total)}
        aria-label={todoTitle(total)}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        onClick={(e) => {
          e.preventDefault()
          /* Deschis, se reciteste: dupa ce omul a rezolvat ceva, cifra se misca. */
          if (!open) void todo.refetch()
          setOpen((v) => !v)
        }}
      >
        <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
        {badge !== '' && <span className="od-todo-badge">{badge}</span>}
      </a>

      <div className="od-todo-panel" role="dialog" aria-label="Needs attention" hidden={!open}>
        <div className="od-todo-head">
          <span className="od-todo-title">Needs attention</span>
          {total > 0 && <span className="od-todo-sum">{total.toLocaleString('en-US')}</span>}
        </div>
        {todo.isError ? (
          <p className="od-todo-quiet">The list could not be read.</p>
        ) : todo.isPending ? (
          <p className="od-todo-quiet">Reading…</p>
        ) : noCompany ? (
          <p className="od-todo-quiet">{TODO_NO_COMPANY}</p>
        ) : items.length === 0 ? (
          <p className="od-todo-quiet">Nothing needs attention right now.</p>
        ) : (
          <ul className="od-todo-list">
            {items.map((item) => {
              const category = TODO_CATEGORIES[item.key]
              return (
                <li key={item.key} data-key={item.key}>
                  <a className="od-todo-row" href={category.href(item)} onClick={go(item)}>
                    <span className="od-todo-line">
                      <span className="od-todo-label">{category.label}</span>
                      <span className="od-todo-count">{item.count.toLocaleString('en-US')}</span>
                      <i className="fas fa-chevron-right od-todo-chevron" aria-hidden="true"></i>
                    </span>
                    <span className="od-todo-hint">{category.hint}</span>
                  </a>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </li>
  )
}
